import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaCheck, FaTimes, FaVolumeUp, FaEye, FaEyeSlash } from 'react-icons/fa';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';
import { getLessonById } from '../data/persianQuestions';
import type { Lesson, QuizQuestion } from '../data/persianQuestions';

const VajakLesson: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<{ [key: number]: string }>({});
  const [quizResults, setQuizResults] = useState<{ [key: number]: boolean }>({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState<number[]>([]);

  useEffect(() => {
    if (lessonId) {
      let foundLesson = undefined;
      if (!isNaN(Number(lessonId))) {
        foundLesson = getLessonById(Number(lessonId));
      }
      if (!foundLesson) {
        foundLesson = getLessonById(lessonId);
      }
      if (foundLesson) {
        setLesson(foundLesson);
      } else {
        navigate('/vajak');
      }
    }
  }, [lessonId, navigate]);

  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem(`vajak-lesson-${lessonId}`);
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setCompletedQuestions(progress.completedQuestions || []);
    }
  }, [lessonId]);

  const saveProgress = () => {
    const progress = {
      completedQuestions,
      completedAt: new Date().toISOString()
    };
    localStorage.setItem(`vajak-lesson-${lessonId}`, JSON.stringify(progress));
    
    // Update overall progress
    const overallProgress = localStorage.getItem('vajak-progress');
    const currentProgress = overallProgress ? JSON.parse(overallProgress) : { completedLessons: [], totalProgress: 0 };
    
    if (!currentProgress.completedLessons.includes(parseInt(lessonId!))) {
      currentProgress.completedLessons.push(parseInt(lessonId!));
      // Fix: Use actual number of lessons instead of hardcoded 5
      const totalLessons = 5; // This should be dynamically calculated based on actual lessons
      currentProgress.totalProgress = Math.min((currentProgress.completedLessons.length / totalLessons) * 100, 100);
      localStorage.setItem('vajak-progress', JSON.stringify(currentProgress));
    }
  };

  const handleQuestionComplete = (questionId: number) => {
    if (!completedQuestions.includes(questionId)) {
      setCompletedQuestions(prev => [...prev, questionId]);
    }
  };

  const handleNextQuestion = () => {
    if (lesson && currentQuestionIndex < lesson.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowAnswer(false);
    } else {
      setShowQuiz(true);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowAnswer(false);
    }
  };

  const handleQuizAnswer = (questionId: number, answer: string) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleQuizSubmit = () => {
    if (!lesson) return;

    const results: { [key: number]: boolean } = {};
    lesson.quiz.forEach((quizQuestion: QuizQuestion) => {
      const userAnswer = quizAnswers[quizQuestion.id];
      results[quizQuestion.id] = userAnswer === quizQuestion.correctAnswer;
    });

    setQuizResults(results);
    setShowQuizResults(true);
    saveProgress();
  };

  const getQuizScore = () => {
    if (!lesson) return 0;
    const correctAnswers = Object.values(quizResults).filter((result: boolean) => result).length;
    return Math.round((correctAnswers / lesson.quiz.length) * 100);
  };

  if (!lesson) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const currentQuestion = lesson.questions[currentQuestionIndex];
  const progress = (completedQuestions.length / lesson.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/vajak"
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Back to Lessons
          </Link>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {lesson.title}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {lesson.persianTitle}
            </p>
          </div>
          <div className="w-24"></div> {/* Spacer for centering */}
        </div>

        {/* Progress Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Progress
            </h3>
            <span className="text-lg font-bold text-orange-500">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Question {currentQuestionIndex + 1} of {lesson.questions.length}</span>
            <span>{completedQuestions.length} completed</span>
          </div>
        </div>

        {!showQuiz ? (
          /* Flashcards Section */
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
              {/* Question Card */}
              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center mb-4">
                    <span className="text-4xl mr-3">{lesson.icon}</span>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Question {currentQuestionIndex + 1}
                    </h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {lesson.description}
                  </p>
                </div>

                {/* Persian Question */}
                <div className="bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Persian Question
                    </h3>
                    {isAudioSupported() && (
                      <button
                        onClick={() => playSmartAudio(undefined, currentQuestion.persian).catch(console.error)}
                        className="p-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors"
                      >
                        <FaVolumeUp />
                      </button>
                    )}
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-4">
                    {currentQuestion.persian}
                  </p>
                  <p className="text-lg text-gray-600 dark:text-gray-400 text-center">
                    {currentQuestion.pronunciation}
                  </p>
                </div>

                {/* Answer Section */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      English Translation
                    </h3>
                    <button
                      onClick={() => setShowAnswer(!showAnswer)}
                      className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      {showAnswer ? <FaEyeSlash className="mr-2" /> : <FaEye className="mr-2" />}
                      {showAnswer ? 'Hide Answer' : 'Show Answer'}
                    </button>
                  </div>
                  
                  {showAnswer ? (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {currentQuestion.english}
                      </p>
                      <div className="flex justify-center space-x-4 rtl:space-x-reverse">
                        <button
                          onClick={() => handleQuestionComplete(currentQuestion.id)}
                          className="flex items-center px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <FaCheck className="mr-2" />
                          I Know This
                        </button>
                        <button
                          onClick={() => handleQuestionComplete(currentQuestion.id)}
                          className="flex items-center px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <FaTimes className="mr-2" />
                          Need Practice
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-gray-600 dark:text-gray-400">
                        Click "Show Answer" to see the English translation
                      </p>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center">
                  <button
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestionIndex === 0}
                    className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                      currentQuestionIndex === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-500 text-white hover:bg-gray-600'
                    }`}
                  >
                    <FaArrowLeft className="mr-2" />
                    Previous
                  </button>

                  <div className="flex space-x-2 rtl:space-x-reverse">
                    {lesson.questions.map((_, index) => (
                      <div
                        key={index}
                        className={`w-3 h-3 rounded-full ${
                          index === currentQuestionIndex
                            ? 'bg-orange-500'
                            : completedQuestions.includes(lesson.questions[index].id)
                            ? 'bg-green-500'
                            : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={handleNextQuestion}
                    className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-semibold"
                  >
                    {currentQuestionIndex === lesson.questions.length - 1 ? (
                      <>
                        Take Quiz
                        <FaArrowRight className="ml-2" />
                      </>
                    ) : (
                      <>
                        Next
                        <FaArrowRight className="ml-2" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Quiz Section */
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
              <div className="p-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    Lesson Quiz
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Test your knowledge with these questions
                  </p>
                </div>

                {!showQuizResults ? (
                  <div className="space-y-8">
                    {lesson.quiz.map((quizQuestion: QuizQuestion, index: number) => (
                      <div key={quizQuestion.id} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                          Question {index + 1}: {quizQuestion.question}
                        </h3>

                        {quizQuestion.type === 'multiple-choice' && quizQuestion.options && (
                          <div className="space-y-3">
                            {quizQuestion.options.map((option: string, optionIndex: number) => (
                              <button
                                key={optionIndex}
                                onClick={() => handleQuizAnswer(quizQuestion.id, option)}
                                className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                                  quizAnswers[quizQuestion.id] === option
                                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-orange-300'
                                }`}
                              >
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {String.fromCharCode(65 + optionIndex)}. {option}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}

                        {quizQuestion.type === 'translation' && (
                          <div className="space-y-3">
                            <input
                              type="text"
                              placeholder="Type your answer..."
                              value={quizAnswers[quizQuestion.id] || ''}
                              onChange={(e) => handleQuizAnswer(quizQuestion.id, e.target.value)}
                              className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-orange-500 focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="text-center">
                      <button
                        onClick={handleQuizSubmit}
                        className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300"
                      >
                        Submit Quiz
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Quiz Results */
                  <div className="text-center">
                    <div className="mb-8">
                      <div className="text-6xl mb-4">🎉</div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Quiz Complete!
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        You scored {getQuizScore()}% on this lesson
                      </p>
                      <div className="text-4xl font-bold text-orange-500">
                        {getQuizScore()}%
                      </div>
                    </div>

                    <div className="space-y-4 mb-8">
                      {lesson.quiz.map((quizQuestion, index) => (
                        <div key={quizQuestion.id} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              Question {index + 1}
                            </span>
                            {quizResults[quizQuestion.id] ? (
                              <FaCheck className="text-green-500 text-xl" />
                            ) : (
                              <FaTimes className="text-red-500 text-xl" />
                            )}
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                            {quizQuestion.question}
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Your answer:</span>{' '}
                            <span className="text-gray-900 dark:text-white">
                              {quizAnswers[quizQuestion.id] || 'No answer'}
                            </span>
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Correct answer:</span>{' '}
                            <span className="text-green-600 dark:text-green-400 font-semibold">
                              {quizQuestion.correctAnswer}
                            </span>
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            {quizQuestion.explanation}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-4">
                      <Link
                        to="/vajak"
                        className="inline-block px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300"
                      >
                        Back to Lessons
                      </Link>
                      <br />
                      <Link
                        to={`/vajak/flashcards/${lesson.id}`}
                        className="inline-block px-8 py-3 border-2 border-orange-500 text-orange-500 dark:text-orange-400 font-semibold rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-300"
                      >
                        Practice with Flashcards
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VajakLesson; 